import React, { createContext, useContext, useState, ReactNode } from "react";
import { useAICredits } from "../hooks/useAICredits";
import AICreditsLimitModal from "../components/common/AICreditsLimitModal";

interface AICreditsContextType {
  hasAICredits: () => boolean;
  balance: number;
  isLoading: boolean;
  handleAIFeatureClick: (callback: () => void) => void;
}

const AICreditsContext = createContext<AICreditsContextType | undefined>(
  undefined
);

export const AICreditsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { balanceQuery, hasAICredits } = useAICredits();
  const [showLimitModal, setShowLimitModal] = useState(false);

  const balance = balanceQuery.data?.totalAvailableCredits ?? 0;
  const isLoading = balanceQuery.isLoading;

  const handleAIFeatureClick = (callback: () => void) => {
    // Avoid treating the loading state as "no credits"
    if (isLoading) {
      return;
    }

    if (!hasAICredits()) {
      setShowLimitModal(true);
      return;
    }

    callback();
  };

  return (
    <AICreditsContext.Provider
      value={{ hasAICredits, balance, isLoading, handleAIFeatureClick }}
    >
      {children}
      {showLimitModal && (
        <AICreditsLimitModal onClose={() => setShowLimitModal(false)} />
      )}
    </AICreditsContext.Provider>
  );
};

export const useAICreditsContext = () => {
  const context = useContext(AICreditsContext);
  if (context === undefined) {
    throw new Error(
      "useAICreditsContext must be used within an AICreditsProvider"
    );
  }
  return context;
};
